import { Printer } from "lucide-react";

import { Button } from "@/shared/components";
import InfoRow from "@/shared/components/Policy/InfoRow";
import PolicyAmountRow from "@/shared/components/Policy/PolicyAmountRow";

import { Status } from "../../../Policies/components/Status";
import {
  INVOICE_CURRENCY_SYMBOL,
  INVOICE_NO_PRINT_CLASS,
  INVOICE_PRINT_AREA_CLASS,
} from "../../constants/invoice";
import { INVOICE_LINE_COLUMNS } from "../../constants/invoiceLineColumns";
import InvoiceLineTable from "../InvoiceLineTable";
import { INVOICE_LOCALISATION } from "../../constants/localisation";
import type { InvoiceDetail, InvoiceParty } from "../../types";

type InvoicePrintModalProps = {
  detail: InvoiceDetail | null;
  onClose: () => void;
};

const formatDate = (dateValue?: string) => {
  if (!dateValue) return "-";

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return "-";

  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// `period` is stored as "from|to" so the print view can show the full range.
const formatPeriodRange = (period: string) => {
  const [from, to] = period.split("|");
  return `${formatDate(from)} - ${formatDate(to)}`;
};

const PartyBlock = ({ title, party }: { title: string; party: InvoiceParty }) => (
  <div className="rounded-lg border border-theme-border bg-theme-surface-soft p-4">
    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-theme-text-soft">
      {title}
    </p>
    <p className="text-sm font-bold text-theme-text">{party.name}</p>
    <p className="mt-1 text-[13px] text-theme-text-soft">{party.address}</p>
    <p className="mt-1 text-[13px] text-theme-text-soft">
      Country: <span className="font-semibold text-theme-text">{party.country}</span>
    </p>
    <p className="text-[13px] text-theme-text-soft">
      {INVOICE_LOCALISATION.taxIdLabel}:{" "}
      <span className="font-semibold text-theme-text">{party.taxId}</span>
    </p>
  </div>
);

export default function InvoicePrintModal({ detail, onClose }: InvoicePrintModalProps) {
  if (!detail) {
    return <p className="py-6 text-center text-sm text-theme-text-soft">Invoice not found.</p>;
  }

  return (
    <div className="space-y-4">
      <div className={`${INVOICE_PRINT_AREA_CLASS} space-y-5`}>
        {/* Document header */}
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-theme-border pb-4">
          <div>
            <h2 className="text-lg font-bold uppercase tracking-wide text-theme-text">
              Tax Invoice
            </h2>
            <p className="mt-1 text-sm font-semibold text-theme-primary">{detail.invoiceNo}</p>
          </div>

          <div className="min-w-[240px] space-y-1.5">
            <InfoRow label="Status" value={<Status status={detail.status} />} />
            <InfoRow label="Invoice date" value={formatDate(detail.invoiceDate)} />
            <InfoRow label="Due date" value={formatDate(detail.dueDate)} />
            <InfoRow label="Billing period" value={formatPeriodRange(detail.period)} />
          </div>
        </div>

        {/* Parties */}
        {/* sm breakpoint so the two blocks stay side by side on a printed page too. */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <PartyBlock title="Billed by" party={detail.billedBy} />
          <PartyBlock title="Billed to" party={detail.billedTo} />
        </div>

        {/* Line items */}
        <InvoiceLineTable columns={INVOICE_LINE_COLUMNS} rows={detail.lineItems} />

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-full max-w-sm space-y-2 rounded-lg border border-theme-border bg-theme-surface-soft p-4">
            <PolicyAmountRow
              label="Taxable value"
              value={detail.taxableValue}
              currencySymbol={INVOICE_CURRENCY_SYMBOL}
            />

            {/* GST rows or a single VAT row, depending on the localisation. */}
            {detail.taxBreakdown.map((taxLine) => (
              <PolicyAmountRow
                key={taxLine.label}
                label={taxLine.label}
                value={taxLine.amount}
                currencySymbol={INVOICE_CURRENCY_SYMBOL}
              />
            ))}

            <div className="border-t border-theme-border pt-2">
              <PolicyAmountRow
                label="Total payable"
                value={detail.grandTotal}
                currencySymbol={INVOICE_CURRENCY_SYMBOL}
                total
              />
            </div>

            <div className="space-y-1.5 border-t border-theme-border pt-2">
              <PolicyAmountRow
                label="Collected"
                value={detail.totalCollected}
                currencySymbol={INVOICE_CURRENCY_SYMBOL}
              />
              <PolicyAmountRow
                label="Outstanding"
                value={detail.outstanding}
                currencySymbol={INVOICE_CURRENCY_SYMBOL}
              />
            </div>
          </div>
        </div>

        <p className="border-t border-theme-border pt-3 text-xs text-theme-text-soft">
          {detail.taxSplit.taxLabel} split:{" "}
          <span className="font-semibold text-theme-text">{detail.taxSplit.label}</span> — broker
          registered in {detail.taxSplit.brokerCountry}, insurer in {detail.taxSplit.insurerCountry}
          . This is a computer generated invoice and does not require a signature.
        </p>
      </div>

      {/* Actions */}
      <div
        className={`${INVOICE_NO_PRINT_CLASS} flex justify-end gap-3 border-t border-theme-border pt-4`}
      >
        <Button type="button" variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button type="button" onClick={() => window.print()}>
          <span className="inline-flex items-center gap-1.5">
            <Printer className="h-4 w-4" />
            Print
          </span>
        </Button>
      </div>
    </div>
  );
}
